import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  type LayoutChangeEvent,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { prefersReducedMotion } from '../../../styles/motion';

/**
 * The scanning-phase hero overlay. Sits on top of the picked image
 * while the backend is doing the initial detection pass — the
 * ~2–4 s silent gap between upload and first `item` event, and the
 * tail end where items keep streaming in.
 *
 * Contract (PRD §8.3):
 *   1. TTFV < 300 ms — this component must render immediately on
 *      mount. Zero async setup, zero await-for-anything.
 *   2. Photo dim + desaturate (brightness 0.75 / saturate 0.9).
 *   3. Gradient sweep bar travels top → bottom on a 1.2 s loop.
 *   4. Chip cycles through generic stages when no live status is
 *      passed; switches to live status (e.g. "Detecting items · 7 found")
 *      the moment SegmentScreen has a real signal to share.
 *   5. `prefers-reduced-motion: reduce` collapses to a static dim +
 *      single chip message — no animation.
 *
 * Sweep bounds are measured from the host container via onLayout —
 * caller does NOT pass a height. Previously the height was hardcoded
 * to 360 px, leaving the line trapped in the top half of the image
 * on most phones.
 */

const CHIP_MESSAGES = [
  'Looking at the room…',
  'Identifying items…',
  'Measuring confidence…',
  'Drawing masks…',
] as const;

const CHIP_INTERVAL_MS = 1400;
const SWEEP_DURATION_MS = 1200;
// Sweep bar is ~80 px tall — start it just above the top edge and
// finish just past the bottom edge so the gradient fully exits before
// the loop restarts.
const SWEEP_BAR_HEIGHT = 80;
const SWEEP_OVERSHOOT = 40;

interface ScanningHeroProps {
  /**
   * Live status text to show in the chip instead of the rotating
   * generic strings. Driven by SegmentScreen from SSE stream state
   * (e.g. "Detecting items · 7 found"). When undefined, the chip
   * falls back to the rotating generic messages.
   */
  statusText?: string;
}

export const ScanningHero: React.FC<ScanningHeroProps> = ({ statusText }) => {
  const [chipIdx, setChipIdx] = useState(0);
  const [heightPx, setHeightPx] = useState(0);
  const reduced = prefersReducedMotion();

  // Chip rotation — only spins when there's no live status to show.
  // Live status replaces the rotation entirely so the chip doesn't
  // flicker between "found 3" and "Measuring confidence…".
  useEffect(() => {
    if (reduced || statusText) return;
    const id = setInterval(() => {
      setChipIdx((i) => (i + 1) % CHIP_MESSAGES.length);
    }, CHIP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reduced, statusText]);

  // Sweep animation — Animated.loop so it chains perfectly when the
  // component stays mounted > 1.2s.
  const sweepY = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (reduced) return;
    sweepY.setValue(0);
    const anim = Animated.loop(
      Animated.timing(sweepY, {
        toValue: 1,
        duration: SWEEP_DURATION_MS,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: Platform.OS !== 'web',
      }),
    );
    anim.start();
    return () => anim.stop();
  }, [reduced, sweepY]);

  // Drive the sweep from the host container's measured height. The
  // line starts above the top edge (-SWEEP_BAR_HEIGHT) and ends past
  // the bottom edge (+SWEEP_OVERSHOOT). Guard against zero-height
  // first frame by giving the interpolation a fallback minimum.
  const safeHeight = Math.max(120, heightPx);
  const translateY = sweepY.interpolate({
    inputRange: [0, 1],
    outputRange: [-SWEEP_BAR_HEIGHT, safeHeight + SWEEP_OVERSHOOT],
  });

  const onLayout = (e: LayoutChangeEvent) => {
    const h = e.nativeEvent.layout.height;
    if (h && Math.abs(h - heightPx) > 1) setHeightPx(h);
  };

  const chipLabel = reduced
    ? 'Analyzing with AI…'
    : (statusText ?? CHIP_MESSAGES[chipIdx]);

  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, styles.dimmer]}
      onLayout={onLayout}
    >
      {/* Sweep bar */}
      {!reduced && (
        <Animated.View
          style={[
            styles.sweep,
            { transform: [{ translateY }] },
          ]}
        >
          <View style={styles.sweepGradientBand} />
        </Animated.View>
      )}

      {/* Status chip */}
      <View style={styles.chipRow} pointerEvents="none">
        <View style={styles.chip}>
          <MaterialIcons
            name="auto-awesome"
            size={14}
            color={fpColors.primary}
          />
          <Text style={styles.chipText} numberOfLines={1}>
            {chipLabel}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  dimmer: {
    // Brightness + saturation are CSS filters; React Native Web
    // passes them through via the `filter` style prop on web.
    // Native platforms render a dim overlay instead (Platform
    // guard in the parent would throw before reaching native
    // anyway — this whole flow is web-only).
    ...(Platform.OS === 'web'
      ? ({ filter: 'brightness(0.82) saturate(0.9)' } as any)
      : { backgroundColor: 'rgba(0,0,0,0.18)' }),
    backgroundColor: 'rgba(0,0,0,0.18)',
    overflow: 'hidden',
  },
  sweep: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: SWEEP_BAR_HEIGHT,
  },
  sweepGradientBand: {
    flex: 1,
    // Subtle purple gradient band — reads as "scanning" without
    // looking like a progress bar.
    ...(Platform.OS === 'web'
      ? ({
          background:
            'linear-gradient(180deg, rgba(125,52,183,0) 0%, rgba(125,52,183,0.28) 45%, rgba(125,52,183,0.4) 50%, rgba(125,52,183,0.28) 55%, rgba(125,52,183,0) 100%)',
          mixBlendMode: 'screen',
        } as any)
      : { backgroundColor: 'rgba(125, 52, 183, 0.25)' }),
  },
  chipRow: {
    position: 'absolute',
    top: 12,
    right: 12,
    flexDirection: 'row',
    // Cap so a long status string doesn't run off the edge of the
    // viewport on narrow phones.
    maxWidth: '90%',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.94)',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    flexShrink: 1,
  },
  chipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.primary,
    letterSpacing: 0.1,
    flexShrink: 1,
  },
});
